import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  FormControl,
  FormLabel,
  FormErrorMessage,
  ModalFooter,
  Input,
} from "@chakra-ui/react";
import { Controller, useForm } from "react-hook-form";
import Message from "@/components/common/AlertMessage";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { File } from "@/types/cloud";
import useSWR from "swr";
import TagSelect from "@/components/common/TagSelect";
import HelpTooltip from "@/components/common/HelpTooltip";

type FormInputs = {
  originalname: string;
  tags: { id: number }[];
};

type Props = {
  id?: number;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
  file: File;
};

const FolderModal = ({
  file,
  isOpen,
  onClose,
  onUpdate,
}: Props): React.ReactElement => {
  const {
    handleSubmit,
    register,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormInputs>();
  const [message, setMessage] = useState(null);
  const { t } = useTranslation("common");
  const { data: tags } = useSWR("/tag");

  const defaultErrorKeys = {
    ServerError: t("errorKeys.serverError"),
    WrongRole: t("errorKeys.wrongRole"),
  };

  const onSubmit = (data) => {
    return axios
      .post(`/cloud/file/${file?.id}`, {
        ...data,
      })
      .then(() => {
        onClose();
        onUpdate();
        setMessage({ success: true });
      })
      .catch((error) => setMessage({ error: defaultErrorKeys[error] }));
  };

  useEffect(() => {
    reset();
    setMessage(null);
  }, [isOpen]);

  return (
    <Modal
      motionPreset="slideInBottom"
      isOpen={isOpen}
      onClose={onClose}
      preserveScrollBarGap
    >
      <ModalOverlay>
        <ModalContent>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <ModalHeader>{t("cloud.editFile")}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {message?.error && (
                <Message status="error" message={message.error} />
              )}
              {message?.success && (
                <Message status="success" message={t("dataSaved")} />
              )}

              <FormControl isInvalid={!!errors.originalname} isRequired>
                <FormLabel htmlFor="originalname">{t("name")}</FormLabel>
                <Input
                  type="text"
                  defaultValue={file?.name}
                  {...register("originalname", { required: true })}
                />
                {errors.originalname && (
                  <FormErrorMessage>
                    {t("users.fieldRequired")}
                  </FormErrorMessage>
                )}
              </FormControl>

              <FormControl mt={5}>
                <FormLabel htmlFor="tags">
                  {t("settings.tags")}
                  <HelpTooltip label="cloud.tags" />
                </FormLabel>
                <Controller
                  name="tags"
                  control={control}
                  defaultValue={file?.tags?.map(({ id }) => ({ id })) || []}
                  render={({ field: { onChange, value } }) => (
                    <TagSelect
                      options={tags}
                      placeholder={t("choose")}
                      value={(value || [])?.map((option) => option.id)}
                      onChange={(values) =>
                        onChange(
                          values?.map((value) => ({
                            id: value.value,
                          }))
                        )
                      }
                      menuPlacement="top"
                      isClearable={true}
                    />
                  )}
                />
              </FormControl>
            </ModalBody>

            <ModalFooter>
              <Button variant="ghost" onClick={onClose}>
                {t("cancel")}
              </Button>
              <Button type="submit" isLoading={isSubmitting} ml={3}>
                {t("save")}
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  );
};

export default FolderModal;
